import React, { useState } from 'react';
import { Button, Form, Card, Alert, Table, InputGroup, Row, Col } from 'react-bootstrap';
import { Plus, Minus, Save, X, Upload, FileText, Image as ImageIcon, Camera } from 'lucide-react';
import { createMultipleProducts } from '../../api/products';

const MultipleProductsForm = ({ onCancel, onSuccess }) => {
    const [products, setProducts] = useState([
        {
            name: '',
            sku: '',
            codeBarre: '',
            basePrice: '',
            prixFournisseur: '',
            pcb: '',
            description: '',
            category: '',
            numeroProduit: 0,
            dimensions: '',
            images: [], // Array to store image files
            mainImageIndex: 0 // Index of the main image
        }
    ]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [csvFile, setCsvFile] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);

    const handleToggleRow = (index) => {
        if (expandedRows.includes(index)) {
            setExpandedRows(expandedRows.filter(i => i !== index));
        } else {
            setExpandedRows([...expandedRows, index]);
        }
    };

    const handleAddProduct = () => {
        setProducts([...products, {
            name: '',
            sku: '',
            codeBarre: '',
            numeroProduit: 0,
            basePrice: '',
            stockQuantity: 0,
            category: '',
            dimensions: '',
            images: [],
            mainImageIndex: 0
        }]);
    };

    const handleRemoveProduct = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);

        // Also remove from expanded rows if needed
        if (expandedRows.includes(index)) {
            setExpandedRows(expandedRows.filter(i => i !== index));
        }
    };

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...products];
        updatedProducts[index][field] = value;
        setProducts(updatedProducts);
    };

    const handleImageUpload = (index, e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const updatedProducts = [...products];
        updatedProducts[index].images = [...updatedProducts[index].images, ...files];
        setProducts(updatedProducts);
    };

    const handleRemoveImage = (productIndex, imageIndex) => {
        const updatedProducts = [...products];
        updatedProducts[productIndex].images.splice(imageIndex, 1);

        // Adjust mainImageIndex if needed
        if (updatedProducts[productIndex].mainImageIndex >= updatedProducts[productIndex].images.length) {
            updatedProducts[productIndex].mainImageIndex = Math.max(0, updatedProducts[productIndex].images.length - 1);
        }

        setProducts(updatedProducts);
    };

    const handleSetMainImage = (productIndex, imageIndex) => {
        const updatedProducts = [...products];
        updatedProducts[productIndex].mainImageIndex = imageIndex;
        setProducts(updatedProducts);
    };

    const handleCsvUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setCsvFile(file);

        const reader = new FileReader();
        reader.onload = (event) => {
            const csvData = event.target.result;
            const lines = csvData.split('\n');

            // Utiliser le point-virgule comme séparateur
            const headers = lines[0].split(';').map(h => h.trim());

            // Map CSV columns to expected fields
            const nameIndex = headers.findIndex(h => h.toLowerCase() === 'name');
            const skuIndex = headers.findIndex(h => h.toLowerCase() === 'sku');
            const numeroProduitIndex = headers.findIndex(h => h.toLowerCase() === 'numero');

            const priceIndex = headers.findIndex(h =>
                h.toLowerCase() === 'price' || h.toLowerCase() === 'baseprice'
            );
            const stockIndex = headers.findIndex(h =>
                h.toLowerCase() === 'stock' || h.toLowerCase() === 'stockquantity'
            );
            const categoryIndex = headers.findIndex(h => h.toLowerCase() === 'category');
            const dimensionsIndex = headers.findIndex(h => h.toLowerCase() === 'dimensions');
            const codeBarreIndex = headers.findIndex(h =>
                ['codebarre', 'code-barre', 'code_barre', 'barcode'].includes(h.toLowerCase())
            );

            const prixFournisseurIndex = headers.findIndex(h =>
                ['prixfournisseur', 'prix_fournisseur', 'prix fournisseur'].includes(h.toLowerCase())
            );
            const pcbIndex = headers.findIndex(h => h.toLowerCase() === 'pcb');
            const descriptionIndex = headers.findIndex(h => h.toLowerCase() === 'description');

            if (nameIndex === -1 || skuIndex === -1 || priceIndex === -1) {
                setError('CSV file must contain at least Name, SKU, and Price columns');
                return;
            }

            // Parse product data avec le point-virgule
            const parsedProducts = [];
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;

                const values = lines[i].split(';').map(v => v.trim());

                const product = {
                    name: values[nameIndex] || '',
                    sku: values[skuIndex] || '',
                    numeroProduit: values[numeroProduitIndex] !== undefined ? parseInt(values[numeroProduitIndex], 10) || 0 : 0,
                    basePrice: values[priceIndex] || '',
                    stockQuantity: values[stockIndex] !== undefined ? parseInt(values[stockIndex], 10) || 0 : 0,
                    category: categoryIndex !== -1 ? values[categoryIndex] || '' : '',
                    dimensions: dimensionsIndex !== -1 ? values[dimensionsIndex] || '' : '',
                    codeBarre: codeBarreIndex !== -1 ? values[codeBarreIndex] || '' : '',
                    prixFournisseur: prixFournisseurIndex !== -1 ? values[prixFournisseurIndex] || '' : '',
                    pcb: pcbIndex !== -1 ? values[pcbIndex] || '' : '',
                    description: descriptionIndex !== -1 ? values[descriptionIndex] || '' : '',
                    images: [],
                    mainImageIndex: 0
                };

                parsedProducts.push(product);
            }

            setProducts(parsedProducts);
        };

        reader.readAsText(file);
    };

    const validateProducts = () => {
        const errors = [];

        products.forEach((product, index) => {
            if (!product.name) {
                errors.push(`Produit #${index + 1}: Le nom est requis`);
            }
            if (!product.sku) {
                errors.push(`Produit #${index + 1}: La référence est requise`);
            }
            if (!product.basePrice || isNaN(parseFloat(product.basePrice))) {
                errors.push(`Produit #${index + 1}: Le prix de base est requis et doit être un nombre`);
            }
            if (product.numeroProduit < 0 || isNaN(product.numeroProduit)) {
                errors.push(`Produit #${index + 1}: Le numéro de produit doit être un nombre positif`);
            }
        });

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Validate all products
        const validationErrors = validateProducts();
        if (validationErrors.length > 0) {
            setError(`Please fix the following errors:\n${validationErrors.join('\n')}`);
            setLoading(false);
            return;
        }

        try {
            // Format product data
            const formattedProducts = products.map(product => ({
                name: product.name,
                sku: product.sku,
                numeroProduit: product.numeroProduit,
                basePrice: parseFloat(product.basePrice),
                prixFournisseur: (product.prixFournisseur !== '' && product.prixFournisseur != null) ? parseFloat(product.prixFournisseur) : null,
                pcb: (product.pcb !== '' && product.pcb != null) ? parseInt(product.pcb, 10) : null,
                categoryName: product.category || '',   // le backend résout via categoryName
                dimensions: product.dimensions,
                codeBarre: product.codeBarre,
                description: product.description || '',
            }));

            // Préparation du FormData
            
// Préparation des arguments pour l'appel API
            const imagesList = products.map(p => p.images || []);
            const mainImageIndices = products.map(p => p.mainImageIndex);

            // Appel via la fonction API (passe par axios `api` : bonne baseURL /soplast/api + token JWT)
            const data = await createMultipleProducts(formattedProducts, imagesList, mainImageIndices);
            console.log('Réponse du serveur:', data);

            setSuccess('Products created successfully!');
            

            // Call the success callback after a short delay
            setTimeout(() => {
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setError(err.message || 'An error occurred. Please try again.');
            console.error('Erreur détaillée:', err);
        } finally {
            setLoading(false);
        }
    };

    const renderImagePreview = (productIndex) => {
        const product = products[productIndex];

        return (
            <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6>Images Produits</h6>
                    <div>
                        <input
                            type="file"
                            id={`image-upload-${productIndex}`}
                            style={{ display: 'none' }}
                            onChange={(e) => handleImageUpload(productIndex, e)}
                            multiple
                            accept="image/*"
                        />
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => document.getElementById(`image-upload-${productIndex}`).click()}
                        >
                            <Camera size={16} className="me-1" />
                            Ajouter Images
                        </Button>
                    </div>
                </div>

                {product.images.length === 0 ? (
                    <p className="text-muted small">Pas d'images chargées</p>
                ) : (
                    <Row className="g-2">
                        {product.images.map((image, imageIndex) => (
                            <Col xs={6} md={4} lg={3} key={imageIndex}>
                                <div className={`position-relative border rounded p-1 ${product.mainImageIndex === imageIndex ? 'border-primary' : ''}`}>
                                    <div className="ratio ratio-1x1">
                                        <img
                                            src={URL.createObjectURL(image)}
                                            alt={`Preview ${imageIndex + 1}`}
                                            className="img-fluid rounded object-fit-cover"
                                        />
                                    </div>
                                    <div className="d-flex justify-content-between mt-1">
                                        <Button
                                            variant={product.mainImageIndex === imageIndex ? "primary" : "outline-secondary"}
                                            size="sm"
                                            className="btn-sm"
                                            onClick={() => handleSetMainImage(productIndex, imageIndex)}
                                        >
                                            {product.mainImageIndex === imageIndex ? "Main" : "Set Main"}
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="btn-sm"
                                            onClick={() => handleRemoveImage(productIndex, imageIndex)}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        );
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">Ajouter plusieurs produits</h5>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger">
                        {error.split('\n').map((line, i) => (
                            <div key={i}>{line}</div>
                        ))}
                    </Alert>
                )}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <h6>Upload CSV (Optional)</h6>
                        <p className="text-muted small">
                            Vous pouvez importer un fichier CSV avec les colonnes suivantes : Nom, Référence, Prix, Stock, Catégorie, Dimensions. La première ligne doit contenir les noms des colonnes.
                        </p>

                        <InputGroup className="mb-3">
                            <Form.Control
                                type="file"
                                accept=".csv"
                                onChange={handleCsvUpload}
                                id="csv-upload"
                            />
                            <Button
                                variant="outline-secondary"
                                onClick={() => document.getElementById('csv-upload').click()}
                            >
                                <Upload size={16} className="me-1" />
                                Upload CSV
                            </Button>
                        </InputGroup>
                    </div>

                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Produits</h6>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleAddProduct}
                            >
                                <Plus size={16} className="me-1" />
                                Ajouter Produit
                            </Button>
                        </div>

                        <div className="table-responsive">
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30px' }}>#</th>
                                        <th>Numéro</th>
                                        <th>Nom*</th>
                                        <th>Référence*</th>
                                        <th>Code-barres</th>
                                        <th>Prix*</th>
                                        <th>Prix fourn.</th>
                                        <th>PCB</th>
                                        <th>Catégorie</th>
                                        <th>Dimensions</th>
                                        <th>Description</th>
                                        <th style={{ width: '100px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product, index) => (
                                        <React.Fragment key={index}>
                                            <tr>
                                                <td>{index + 1}</td>
                                                
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        onWheel={(e) => e.target.blur()}
                                                        value={product.numeroProduit}
                                                        onChange={(e) => handleProductChange(index, 'numeroProduit', e.target.value)}
                                                        size="sm"
                                                        min="0"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.name}
                                                        onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                                                        required
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.sku}
                                                        onChange={(e) => handleProductChange(index, 'sku', e.target.value)}
                                                        required
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.codeBarre || ''}
                                                        onChange={(e) => handleProductChange(index, 'codeBarre', e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.basePrice}
                                                        onChange={(e) => handleProductChange(index, 'basePrice', e.target.value)}
                                                        required
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.prixFournisseur || ''}
                                                        onChange={(e) => handleProductChange(index, 'prixFournisseur', e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        onWheel={(e) => e.target.blur()}
                                                        value={product.pcb || ''}
                                                        onChange={(e) => handleProductChange(index, 'pcb', e.target.value)}
                                                        size="sm"
                                                        min="0"
                                                    />
                                                </td>

                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.category}
                                                        onChange={(e) => handleProductChange(index, 'category', e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.dimensions}
                                                        onChange={(e) => handleProductChange(index, 'dimensions', e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={product.description || ''}
                                                        onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                                                        size="sm"
                                                    />
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleToggleRow(index)}
                                                            title="Add images"
                                                        >
                                                            <ImageIcon size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveProduct(index)}
                                                            disabled={products.length <= 1}
                                                            title="Remove product"
                                                        >
                                                            <Minus size={16} />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedRows.includes(index) && (
                                                <tr>
                                                    <td colSpan="12" className="bg-light">
                                                        {renderImagePreview(index)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading || products.length === 0}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Création Produits...
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    Créer Produits
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default MultipleProductsForm;